import type { ActionId, Difficulty, JournalLayer } from "./types";

export const SAVE_FORMAT_VERSION = 1;
export const SAVE_STORAGE_KEY = "where-rivers-ran:save:v1";
export const THEME_STORAGE_KEY = "where-rivers-ran:theme";

export type ThemePreference = "automatic" | "dark" | "light";
export type SavedPhase = "playing" | "failed" | "complete";

export type CommittedAction = Readonly<{
  cellKey: string;
  action: ActionId;
}>;

export type SavedGame = Readonly<{
  formatVersion: number;
  siteId: string;
  schemaVersion: number;
  rulesetVersion: number;
  contentVersion: number;
  phase: SavedPhase;
  difficulty: Difficulty;
  selectedKey: string;
  committedActions: readonly CommittedAction[];
  journal: Readonly<Record<JournalLayer, readonly string[]>>;
  hintSteps: Readonly<Record<string, number>>;
}>;

const ACTION_IDS: readonly ActionId[] = [
  "oats",
  "wheat",
  "alfalfa",
  "survey",
  "excavate",
  "preserve",
];
const DIFFICULTIES: readonly Difficulty[] = ["easy", "standard", "hard"];
const PHASES: readonly SavedPhase[] = ["playing", "failed", "complete"];
const THEMES: readonly ThemePreference[] = ["automatic", "dark", "light"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isKnownCellKeys(values: readonly string[], cellKeys: ReadonlySet<string>): boolean {
  return values.every((value) => cellKeys.has(value));
}

export function parseThemePreference(value: string | null): ThemePreference {
  return THEMES.includes(value as ThemePreference)
    ? (value as ThemePreference)
    : "automatic";
}

export function parseSavedGame(
  raw: string | null,
  expected: Readonly<{
    siteId: string;
    schemaVersion: number;
    rulesetVersion: number;
    contentVersion: number;
    cellKeys: ReadonlySet<string>;
  }>,
): SavedGame | undefined {
  if (raw === null) return undefined;

  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!isRecord(value)) return undefined;

  if (
    value.formatVersion !== SAVE_FORMAT_VERSION ||
    value.siteId !== expected.siteId ||
    value.schemaVersion !== expected.schemaVersion ||
    value.rulesetVersion !== expected.rulesetVersion ||
    value.contentVersion !== expected.contentVersion ||
    !PHASES.includes(value.phase as SavedPhase) ||
    !DIFFICULTIES.includes(value.difficulty as Difficulty) ||
    typeof value.selectedKey !== "string" ||
    !expected.cellKeys.has(value.selectedKey) ||
    !Array.isArray(value.committedActions) ||
    !isRecord(value.journal) ||
    !isStringArray(value.journal.water) ||
    !isStringArray(value.journal.road) ||
    !isStringArray(value.journal.interest) ||
    !isRecord(value.hintSteps)
  ) {
    return undefined;
  }

  const committedActions: CommittedAction[] = [];
  for (const item of value.committedActions) {
    if (
      !isRecord(item) ||
      typeof item.cellKey !== "string" ||
      !expected.cellKeys.has(item.cellKey) ||
      !ACTION_IDS.includes(item.action as ActionId)
    ) {
      return undefined;
    }
    committedActions.push({
      cellKey: item.cellKey,
      action: item.action as ActionId,
    });
  }

  const journal = {
    water: [...value.journal.water],
    road: [...value.journal.road],
    interest: [...value.journal.interest],
  };
  if (
    !isKnownCellKeys(journal.water, expected.cellKeys) ||
    !isKnownCellKeys(journal.road, expected.cellKeys) ||
    !isKnownCellKeys(journal.interest, expected.cellKeys)
  ) {
    return undefined;
  }

  const hintSteps: Record<string, number> = {};
  for (const [key, step] of Object.entries(value.hintSteps)) {
    if (
      !expected.cellKeys.has(key) ||
      typeof step !== "number" ||
      !Number.isInteger(step) ||
      step < 0 ||
      step > 3
    ) {
      return undefined;
    }
    hintSteps[key] = step;
  }

  return {
    formatVersion: SAVE_FORMAT_VERSION,
    siteId: expected.siteId,
    schemaVersion: expected.schemaVersion,
    rulesetVersion: expected.rulesetVersion,
    contentVersion: expected.contentVersion,
    phase: value.phase as SavedPhase,
    difficulty: value.difficulty as Difficulty,
    selectedKey: value.selectedKey,
    committedActions,
    journal,
    hintSteps,
  };
}
